import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api-client";
import { withOverrideRetries } from "@/lib/reservation-api";
import { useAppStore } from "@/store/store";
import { useAuthStore } from "@/store/auth-store";
import type {
  ApiWaitlistCreate,
  ApiWaitlistEntry,
  ApiWaitlistTableSuggestion,
  ApiWaitlistUpdate,
  PaginatedResponse,
} from "@/types/api";

export function useWaitlist() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const organizationId = useAuthStore((s) => s.organizationId);
  const locationId = useAuthStore((s) => s.locationId);

  return useQuery({
    queryKey: ["waitlist", organizationId, locationId],
    // Pull every entry for the location's current day and filter
    // client-side. The backend's ``?status=`` filter only accepts a
    // single status, but the queue card needs to surface both
    // ``waiting`` (still to be notified) and ``notified`` (table is
    // ready, awaiting the guest's arrival). Filtering here keeps the
    // backend filter generic.
    queryFn: () =>
      api
        .get("waitlist/")
        .json<PaginatedResponse<ApiWaitlistEntry>>()
        .then((r) => r.items.filter((e) => e.status === "waiting" || e.status === "notified")),
    enabled: isAuthenticated && !!organizationId && !!locationId,
  });
}

/** Adds a walk-in guest to the waiting queue (staff modal). */
export function useWaitlistCreate() {
  const qc = useQueryClient();
  const organizationId = useAuthStore((s) => s.organizationId);
  const locationId = useAuthStore((s) => s.locationId);

  return useMutation({
    mutationFn: (body: ApiWaitlistCreate) =>
      api.post("waitlist/", { json: body }).json<ApiWaitlistEntry>(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["waitlist", organizationId, locationId] }),
  });
}

/** Marks a waiting entry as ``notified`` (host told the guest the table
 * is ready). Doesn't seat — that's a separate step once the guest
 * physically arrives. The guest-facing page polls and switches to the
 * "tu mesa está lista" view as soon as this lands. */
export function useWaitlistNotify() {
  const qc = useQueryClient();
  const organizationId = useAuthStore((s) => s.organizationId);
  const locationId = useAuthStore((s) => s.locationId);

  return useMutation({
    mutationFn: (id: string) =>
      api.post(`waitlist/${id}/notify/`, { json: {} }).json<ApiWaitlistEntry>(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["waitlist", organizationId, locationId] }),
  });
}

/** Marks a waiting entry as no-show and refreshes the queue. */
export function useWaitlistNoShow() {
  const qc = useQueryClient();
  const organizationId = useAuthStore((s) => s.organizationId);
  const locationId = useAuthStore((s) => s.locationId);

  return useMutation({
    mutationFn: (id: string) => api.post(`waitlist/${id}/no-show/`, { json: {} }).json(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["waitlist", organizationId, locationId] }),
  });
}

/** Undo a no-show — puts the entry back in the queue (``waiting``). Powers
 * the "Revertir" button on the no-show toast. */
export function useWaitlistRevertNoShow() {
  const qc = useQueryClient();
  const organizationId = useAuthStore((s) => s.organizationId);
  const locationId = useAuthStore((s) => s.locationId);

  return useMutation({
    mutationFn: (id: string) =>
      api.post(`waitlist/${id}/revert-no-show/`, { json: {} }).json<ApiWaitlistEntry>(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["waitlist", organizationId, locationId] }),
  });
}

/** Patches a waiting entry (e.g. internal notes, pax, zone) and refreshes the
 * queue. Mirrors the reservation inline-notes autosave. */
export function useWaitlistUpdate() {
  const qc = useQueryClient();
  const organizationId = useAuthStore((s) => s.organizationId);
  const locationId = useAuthStore((s) => s.locationId);

  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: ApiWaitlistUpdate }) =>
      api.patch(`waitlist/${id}/`, { json: body }).json<ApiWaitlistEntry>(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["waitlist", organizationId, locationId] });
      // pax / preferred_zone / features all feed the suggestion matcher,
      // so a patch may flip the recommended table. Invalidate suggestions
      // for every entry — cheap and avoids stale chips after an edit.
      qc.invalidateQueries({ queryKey: ["waitlist-table-suggestions"] });
    },
  });
}

/** Free table groups that fit a waiting entry right now, best first. Each
 * waiting card reads the top group to show + seat on it, so we cache briefly;
 * seating invalidates this key (availability changed). */
export function useWaitlistTableSuggestions(entryId: string | null) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const locationId = useAuthStore((s) => s.locationId);

  return useQuery({
    queryKey: ["waitlist-table-suggestions", entryId, locationId],
    // The backend Router paginates every list response, so this endpoint
    // returns a {items, count} envelope — unwrap it like useWaitlist does.
    queryFn: () =>
      api
        .get(`waitlist/${entryId}/table-suggestions/`)
        .json<PaginatedResponse<ApiWaitlistTableSuggestion>>()
        .then((r) => r.items),
    enabled: isAuthenticated && !!locationId && !!entryId,
    staleTime: 30_000,
  });
}

/** Seats a waiting entry on the chosen tables → backend creates a walk-in
 * reservation and closes the entry. Refreshes the queue and the reservation
 * list/map (the new walk-in must appear), plus the suggestion cache. */
type SeatMove = { reservation_id: string; tables: string[] };

export function useWaitlistSeat() {
  const qc = useQueryClient();
  const organizationId = useAuthStore((s) => s.organizationId);
  const locationId = useAuthStore((s) => s.locationId);

  // One toast copy per dismissed override — same shape as
  // ``apiCreateReservation`` so the host's mental model carries over
  // from creating a reservation to seating from the waitlist.
  const cancelMessages = {
    after_hours: "Se respetó el horario público.",
    kitchen: "El tope de cocina se respetó.",
    pin: "Se respetaron los pines.",
    constraints: "Selección de mesas no confirmada.",
  } as const;
  return useMutation({
    mutationFn: ({
      id,
      tableIds,
      moves,
    }: {
      id: string;
      tableIds: string[];
      // Pre-emptive moves coming from a TableExplorer redistribute pick.
      // Applied atomically before the seat by the backend.
      moves?: SeatMove[];
    }) =>
      withOverrideRetries(
        (body) => api.post(`waitlist/${id}/seat/`, { json: body }).json<ApiWaitlistEntry>(),
        {
          table_ids: tableIds,
          moves: moves ?? [],
        },
        {
          onDismiss: (kind) => {
            useAppStore.getState().notify("info", "Sentado cancelado", cancelMessages[kind]);
          },
        },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["waitlist", organizationId, locationId] });
      qc.invalidateQueries({ queryKey: ["reservations", organizationId] });
      qc.invalidateQueries({ queryKey: ["waitlist-table-suggestions"] });
    },
  });
}
